import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Briefcase, Loader2, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { porExtenso } from 'numero-por-extenso';

// Lista de UFs brasileiras em ordem alfabética
const listaUFs = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT",
  "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"
];

export default function ProLabore() {
  const [formData, setFormData] = useState({
    cnpj: '',
    razaoSocial: '',
    valorProLabore: '',
    nome: '',
    cpf: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    competencia: '',
    gerarTresUltimas: false,
    // Novos campos para o recibo
    municipioEmpresa: '',
    ufEmpresa: '',
  });
  
  // Estado para armazenar lista de municípios por UF
  const [municipios, setMunicipios] = useState([]);

  const [calculatedValues, setCalculatedValues] = useState({
    inss: 0,
    irrf: 0,
    liquido: 0,
  });

  const [loadingCep, setLoadingCep] = useState(false);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [error, setError] = useState(null);
  
  // Função para buscar municípios de uma UF
  const buscarMunicipiosPorUF = async (uf) => {
    if (!uf) return;
    
    try {
      setLoadingMunicipios(true);
      setError(null);
      const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`);
      if (!response.ok) throw new Error('Erro ao buscar municípios');
      
      const data = await response.json();
      setMunicipios(data.map(municipio => municipio.nome).sort());
    } catch (err) {
      setError(`Erro ao carregar municípios: ${err.message}`);
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  };
  
  // Função para obter o último dia de um mês/ano
  const getUltimoDiaMes = (mes, ano) => {
    // O mês em JavaScript é 0-indexed (0 = janeiro, 11 = dezembro)
    // Ao passar dia 0 do próximo mês, obtemos o último dia do mês atual
    return new Date(ano, mes, 0).getDate();
  };

  // Calcula os descontos e o valor líquido
  // Efeito para carregar municípios quando o componente é montado, se a UF já estiver selecionada
  useEffect(() => {
    if (formData.ufEmpresa) {
      buscarMunicipiosPorUF(formData.ufEmpresa);
    }
  }, []);
  
  useEffect(() => {
    // Converte o valor formatado com pontos e vírgulas para número
    const valorFormatado = formData.valorProLabore || '0';
    const valorLimpo = valorFormatado.replace(/\./g, '').replace(',', '.'); // Remove pontos e troca vírgula por ponto
    const valor = parseFloat(valorLimpo) || 0;
    
    if (valor === 0) {
      setCalculatedValues({ inss: 0, irrf: 0, liquido: 0 });
      return;
    }

    // 1. Cálculo do INSS (11% sobre o valor, limitado ao teto)
    const tetoInss = 8157.41; // Teto para 2025, conforme solicitado
    const baseInss = Math.min(valor, tetoInss);
    const valorInss = baseInss * 0.11;

    // 2. Cálculo do IRRF (tabela progressiva)
    const baseIrrf = valor - valorInss;
    let aliquotaIrrf = 0;
    let parcelaDeduzirIrrf = 0;

    // Tabela de referência (2024). O ideal é buscar isso de uma API ou config.
    if (baseIrrf > 4664.68) {
      aliquotaIrrf = 0.275;
      parcelaDeduzirIrrf = 896.00;
    } else if (baseIrrf > 3751.05) {
      aliquotaIrrf = 0.225;
      parcelaDeduzirIrrf = 662.77;
    } else if (baseIrrf > 2826.65) {
      aliquotaIrrf = 0.15;
      parcelaDeduzirIrrf = 381.44;
    } else if (baseIrrf > 2259.20) {
      aliquotaIrrf = 0.075;
      parcelaDeduzirIrrf = 169.44;
    }

    const valorIrrf = (baseIrrf * aliquotaIrrf) - parcelaDeduzirIrrf;
    const irrfFinal = Math.max(0, valorIrrf); // IRRF não pode ser negativo

    // 3. Cálculo do Valor Líquido
    const valorLiquido = valor - valorInss - irrfFinal;

    setCalculatedValues({
      inss: valorInss,
      irrf: irrfFinal,
      liquido: valorLiquido,
    });

  }, [formData.valorProLabore]);

  const maskCpf = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .substring(0, 14);
  };

  const maskCnpj = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  const maskCep = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 9);
  };

  const maskCompetencia = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .substring(0, 7);
  };
  
  const maskValorProLabore = (value) => {
    // Remove qualquer caractere que não seja número ou vírgula
    value = value.replace(/[^\d,]/g, '');
    
    // Garantir que tenha apenas uma vírgula
    const partes = value.split(',');
    if (partes.length > 2) {
      value = partes[0] + ',' + partes.slice(1).join('');
    }
    
    // Separar parte inteira e decimal
    let [parteInteira, parteDecimal] = value.split(',');
    
    // Formatar parte inteira com pontos a cada três dígitos
    parteInteira = parteInteira.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Limitar parte decimal a dois dígitos
    if (parteDecimal) {
      parteDecimal = parteDecimal.substring(0, 2);
      return `${parteInteira},${parteDecimal}`;
    }
    
    return parteInteira + (value.includes(',') ? ',' : '');
  }

  const handleInputChange = (e) => {
    let { id, value, type, checked } = e.target;
    
    if (id === 'cpf') value = maskCpf(value);
    if (id === 'cnpj') value = maskCnpj(value);
    if (id === 'cep') value = maskCep(value);
    if (id === 'competencia') value = maskCompetencia(value);
    if (id === 'valorProLabore') value = maskValorProLabore(value);

    setFormData((prev) => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
    
    // Se a UF da empresa foi alterada, carregue os municípios correspondentes
    if (id === 'ufEmpresa' && value) {
      buscarMunicipiosPorUF(value);
      // Limpa o município quando a UF muda
      setFormData(prev => ({ ...prev, municipioEmpresa: '' }));
    }
  };

  const handleCepBlur = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (cep.length !== 8) {
      return;
    }

    setLoadingCep(true);
    setError(null);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data.erro) {
        throw new Error("CEP não encontrado.");
      }
      setFormData((prev) => ({
        ...prev,
        endereco: data.logradouro,
        bairro: data.bairro,
        cidade: data.localidade,
        uf: data.uf,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCep(false);
    }
  };

  const getCompetencias = () => {
    const { competencia, gerarTresUltimas } = formData;
    if (!competencia || !/^\d{2}\/\d{4}$/.test(competencia)) {
      return [];
    }

    const [mes, ano] = competencia.split('/').map(Number);
    const dataBase = new Date(ano, mes - 1, 1);
    const competencias = [competencia];

    if (gerarTresUltimas) {
      for (let i = 1; i < 3; i++) {
        const dataAnterior = new Date(dataBase);
        dataAnterior.setMonth(dataBase.getMonth() - i);
        const mesAnterior = (dataAnterior.getMonth() + 1).toString().padStart(2, '0');
        const anoAnterior = dataAnterior.getFullYear();
        competencias.push(`${mesAnterior}/${anoAnterior}`);
      }
    }
    return competencias;
  };

  const drawReceiptContent = (doc, yOffset, competencia) => {
    const { razaoSocial, cnpj, nome, cpf, endereco, numero, complemento, bairro, cidade, uf, valorProLabore, cep } = formData;
    const { inss, irrf, liquido } = calculatedValues;
    
    // Converter o valor formatado (com pontos e vírgulas) para número
    const valorFormatado = valorProLabore || '0';
    const valorLimpo = valorFormatado.replace(/\./g, '').replace(',', '.'); // Remove pontos e troca vírgula por ponto
    const valorBruto = parseFloat(valorLimpo) || 0;

    const pageMargin = 10;
    const receiptWidth = doc.internal.pageSize.width - 2 * pageMargin;
    const startX = pageMargin;
    const startY = yOffset + pageMargin;

    let currentY = startY;
    const lineHeight = 4.5; // Altura de linha padrão para textos gerais
    const smallLineHeight = 4; // Altura de linha para itens de lista (dados da empresa)
    const blockPadding = 3; // Aumentado para dar mais respiro

    // --- Bloco Superior: Título e Valores ---
    const block1Top = currentY;
    const block1Col1Width = receiptWidth * 0.50; // Ajustado para dar mais espaço aos valores
    // const block1Col2Width = receiptWidth * 0.50;

    // Coluna 2: Valores (desenhada primeiro para calcular altura do bloco)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    let valuesY = block1Top + 5;
    const labelColX = startX + block1Col1Width + blockPadding; 
    const valueAlignRightX = startX + receiptWidth - blockPadding - 5; // X para alinhar valores à direita

    const drawValueLine = (label, value, isBold = false) => {
      if (isBold) doc.setFont('helvetica', 'bold');
      doc.text(label, labelColX, valuesY);
      doc.text(value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), valueAlignRightX, valuesY, { align: 'right' });
      if (isBold) doc.setFont('helvetica', 'normal');
      valuesY += lineHeight;
    };
    // Ajuste nos pontilhados para consistência e evitar sobreposição
    drawValueLine('Valor Bruto................:', valorBruto);
    drawValueLine('Pensão Alimentícia.......:', 0.00);
    drawValueLine('INSS.......................:', inss);
    drawValueLine('IRRF.......................:', irrf);
    drawValueLine('Outros Proventos e Sal Fam.:', 0.00); // Rótulo longo, menos pontos
    drawValueLine('Outros Descontos.........:', 0.00);
    valuesY += 1; // Espaço extra antes do líquido
    drawValueLine('Líquido Recebido.........:', liquido, true);
    
    const block1Height = valuesY - block1Top + blockPadding; 

    // Coluna 1: Título (desenhado após saber a altura do bloco1)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const titleText = 'RECIBO DE PRÓ-LABORE';
    const titleWidth = doc.getTextWidth(titleText);
    // Centraliza verticalmente o título no bloco1
    const titleTextHeight = doc.getTextDimensions(titleText).h;
    doc.text(titleText, startX + (block1Col1Width - titleWidth) / 2, block1Top + (block1Height / 2) + (titleTextHeight / 4) );

    doc.rect(startX, block1Top, receiptWidth, block1Height);
    doc.line(startX + block1Col1Width, block1Top, startX + block1Col1Width, block1Top + block1Height);
    currentY = block1Top + block1Height + blockPadding;

    const block3Height = lineHeight * 3.5 + blockPadding * 2; // Altura do Bloco 3, um pouco mais alta

    // Obter o último dia do mês da competência
    const [mesStr, anoStr] = competencia.split('/');
    const mes = parseInt(mesStr);
    const ano = parseInt(anoStr);
    const ultimoDiaMes = getUltimoDiaMes(mes, ano);
    
    // Formatar data com o último dia do mês da competência
    const dia = ultimoDiaMes.toString().padStart(2, '0');
    const nomeMes = new Date(ano, mes - 1, 1).toLocaleString('pt-BR', { month: 'long' });
    
    // Usar município e UF da empresa especificados pelo usuário
    const municipioTexto = formData.municipioEmpresa || '[Município não informado]';
    const ufTexto = formData.ufEmpresa || '[UF não informada]';
    const localData = `${municipioTexto} - ${ufTexto}, ${dia} de ${nomeMes} de ${ano}.`;

    // --- Bloco Intermediário 1: Texto Recebimento ---
    const block2Top = currentY;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const valorPorExtenso = porExtenso(liquido, 'monetario');
    
    // Converter a competência para formato de mês por extenso
    const mesAno = competencia.split('/');
    const mesNumero = parseInt(mesAno[0]);
    const anoCompetencia = mesAno[1];
    const mesesPorExtenso = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const mesPorExtenso = mesesPorExtenso[mesNumero - 1];
    const competenciaPorExtenso = `${mesPorExtenso} de ${anoCompetencia}`;
    
    // Novo texto formatado
    const textoRecibo = `Recebi da empresa ${razaoSocial || '[Empresa não informada]'}, inscrita no CNPJ N°: ${cnpj || '[CNPJ não informado]'} a importância acima de R$ ${liquido.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')} (${valorPorExtenso}), referente ao meu pró-labore do mês de ${competenciaPorExtenso} com os descontos exigidos em lei.`;
    const splitReciboText = doc.splitTextToSize(textoRecibo, receiptWidth - (blockPadding * 2));
    const actualBlock2Height = block3Height; // Harmoniza altura com Bloco 3

    const textActualHeightB2 = doc.getTextDimensions(splitReciboText).h;
    const ascentB2 = doc.getFontSize() * 0.69; // Fator de ascendência para ajuste fino
    
    // Centraliza o texto verticalmente no bloco 2 com ajuste suave para cima
    const paddingTopB2 = (actualBlock2Height - textActualHeightB2) / 2;
    const verticalAdjustment = doc.getFontSize() * 0.3; // Ajuste reduzido para posicionar o texto mais abaixo
    const reciboTextBaselineY = block2Top + paddingTopB2 + ascentB2 - verticalAdjustment;
    
    // Adiciona mais padding nos lados para melhorar aparência
    const horizontalPadding = blockPadding * 1.2;
    doc.text(splitReciboText, startX + horizontalPadding, reciboTextBaselineY);
    doc.rect(startX, block2Top, receiptWidth, actualBlock2Height);
    currentY += actualBlock2Height + blockPadding;

    // --- Bloco Intermediário 2: Local/Data e Assinatura (Desenho efetivo) ---
    const block3ActualTop = currentY;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    const localDataDim = doc.getTextDimensions(localData);
    const ascentDate = doc.getFontSize() * 0.75; // Fator de ascendência ajustado
    const paddingTopDate = (block3Height - localDataDim.h) / 2;
    const final_date_baseline_Y = block3ActualTop + paddingTopDate + ascentDate;
    doc.text(localData, startX + blockPadding, final_date_baseline_Y);

    const signatureAreaX = startX + receiptWidth / 2;
    const signatureAreaWidth = receiptWidth / 2;
    const signatureLineWidth = signatureAreaWidth * 0.7;
    const signatureLineXStart = signatureAreaX + (signatureAreaWidth - signatureLineWidth) / 2;
    const signatureText = 'Assinatura';
    doc.line(signatureLineXStart, final_date_baseline_Y, signatureLineXStart + signatureLineWidth, final_date_baseline_Y);
    
    const ascentSigText = doc.getFontSize() * 0.69;
    const gapBelowLine = doc.getFontSize() * (-0.2); // Valor negativo maior para puxar o texto bem mais para cima
    const baselineSigText = final_date_baseline_Y + gapBelowLine + ascentSigText;
    const signatureTextWidth = doc.getTextWidth(signatureText);
    doc.text(signatureText, signatureAreaX + (signatureAreaWidth - signatureTextWidth) / 2, baselineSigText);
    
    doc.rect(startX, block3ActualTop, receiptWidth, block3Height);
    currentY += block3Height + blockPadding;

    // --- Bloco Inferior: Dados Empresa/Sócio ---
    const block4Top = currentY;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');

    // Definimos todas as linhas do bloco 4
    const col1Lines = [
      `Empresa: ${razaoSocial || ''}`, 
      `CNPJ: ${cnpj || ''}`, 
      `Nome: ${nome || ''}`, 
      `CPF: ${cpf || ''}`, 
      `Endereço: ${endereco || ''}${numero ? ', ' + numero : ''}${complemento ? ' - ' + complemento : ''}`, 
      `CEP: ${cep || ''}`
    ];
    
    const col2Lines = [
      `Bairro: ${bairro || ''}`, 
      `UF: ${uf || ''}`, 
      `Cidade: ${cidade || ''}`
    ];

    // Calculamos a altura necessária para todas as linhas
    const block4LineHeight = doc.getFontSize() * 1.15;
    const maxLines = Math.max(col1Lines.length, col2Lines.length);
    const contentHeight = maxLines * block4LineHeight;
    
    // Adicionamos padding superior e inferior proporcional
    const verticalPadding = doc.getFontSize() * 1.0;
    const actualBlock4Height = contentHeight + (verticalPadding * 2);
    
    const ascentB4 = doc.getFontSize() * 0.69;
    const textBlock4BaselineStartY = block4Top + verticalPadding + ascentB4;

    // Desenhar Coluna 1
    let currentDrawY = textBlock4BaselineStartY;
    col1Lines.forEach(line => {
      doc.text(line, startX + blockPadding, currentDrawY);
      currentDrawY += block4LineHeight;
    });

    // Desenhar Coluna 2
    currentDrawY = textBlock4BaselineStartY; // Reset Y para a segunda coluna
    col2Lines.forEach(line => {
      doc.text(line, startX + receiptWidth / 2 + blockPadding, currentDrawY);
      currentDrawY += block4LineHeight;
    });

    doc.rect(startX, block4Top, receiptWidth, actualBlock4Height);
    // currentY += actualBlock4Height; // Não precisa mais, é o último bloco
  };

  const generateSinglePdf = (competencia) => {
    const { nome } = formData; // Garante que 'nome' esteja acessível para o nome do arquivo
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageHeight = doc.internal.pageSize.height;

    // Desenha o recibo
    drawReceiptContent(doc, 0, competencia);

    // // Linha divisória (opcional) - Comentado
    // // doc.line(10, pageHeight / 2, doc.internal.pageSize.width - 10, pageHeight / 2);

    // // Desenha o segundo recibo na metade inferior - Removido para ter apenas um recibo
    // drawReceiptContent(doc, pageHeight / 2, competencia);

    // Usa o nome do sócio para nomear o arquivo, tratando caso não exista
    const nomeArquivo = nome ? nome.split(' ')[0] : 'ProLabore';
    doc.save(`Recibo_ProLabore_${nomeArquivo}_${competencia.replace('/', '-')}.pdf`);
  };

  const handleGenerateRecibo = () => {
    setError(null);
    const { razaoSocial, cnpj, nome, cpf, valorProLabore, competencia } = formData;
    const camposObrigatorios = { razaoSocial, cnpj, nome, cpf, valorProLabore, competencia };

    const campoVazio = Object.keys(camposObrigatorios).find(key => !camposObrigatorios[key]);

    if (campoVazio) {
      setError(`O campo '${campoVazio}' é obrigatório.`);
      return;
    }

    if (!/^\d{2}\/\d{4}$/.test(competencia)) {
        setError('O formato da competência deve ser MM/AAAA.');
        return;
    }

    const competenciasParaGerar = getCompetencias();
    competenciasParaGerar.forEach(comp => {
      generateSinglePdf(comp);
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <header className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
        <div className="flex items-center space-x-4">
          <Link to={createPageUrl("Operations")}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold text-gray-800 flex items-center">
            <Briefcase className="w-6 h-6 mr-2 text-purple-600" />
            Gerador de Recibo de Pró-Labore
          </h1>
        </div>
      </header>

      <main className="flex-1 p-6 overflow-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          {error && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-3 text-red-500" />
                <p className="text-sm font-medium text-red-700">{error}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Dados da Empresa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input id="cnpj" type="text" value={formData.cnpj} onChange={handleInputChange} placeholder="00.000.000/0000-00" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="razaoSocial">Razão Social</Label>
                  <Input id="razaoSocial" type="text" value={formData.razaoSocial} onChange={handleInputChange} placeholder="Nome da Empresa LTDA" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ufEmpresa">UF da Empresa (para o Recibo)</Label>
                  <select 
                    id="ufEmpresa" 
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.ufEmpresa} 
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione a UF</option>
                    {listaUFs.map(uf => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="municipioEmpresa">Município da Empresa (para o Recibo)</Label>
                  <select 
                    id="municipioEmpresa" 
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.municipioEmpresa} 
                    onChange={handleInputChange}
                    disabled={!formData.ufEmpresa || loadingMunicipios}
                  >
                    <option value="">{loadingMunicipios ? 'Carregando...' : 'Selecione o Município'}</option>
                    {municipios.map(municipio => (
                      <option key={municipio} value={municipio}>{municipio}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dados do Sócio</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome Completo</Label>
                <Input id="nome" type="text" value={formData.nome} onChange={handleInputChange} placeholder="Nome do Sócio" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <Input id="cpf" type="text" value={formData.cpf} onChange={handleInputChange} placeholder="000.000.000-00" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Endereço</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP</Label>
                  <div className="flex items-center">
                    <Input id="cep" type="text" value={formData.cep} onChange={handleInputChange} onBlur={handleCepBlur} placeholder="00000-000" />
                    {loadingCep && <Loader2 className="ml-2 h-5 w-5 animate-spin" />}
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input id="endereco" value={formData.endereco} onChange={handleInputChange} placeholder="Rua, Avenida, etc."/>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                 <div className="space-y-2">
                  <Label htmlFor="numero">Número</Label>
                  <Input id="numero" value={formData.numero} onChange={handleInputChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input id="complemento" value={formData.complemento} onChange={handleInputChange} placeholder="Apto, Bloco, etc."/>
                </div>
                 <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input id="bairro" value={formData.bairro} onChange={handleInputChange} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input id="cidade" value={formData.cidade} onChange={handleInputChange} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uf">UF</Label>
                  <Input id="uf" value={formData.uf} onChange={handleInputChange} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Valores e Competência</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valorProLabore">Valor do Pró-Labore (Bruto)</Label>
                  <Input id="valorProLabore" type="text" value={formData.valorProLabore} onChange={handleInputChange} placeholder="R$ 0,00"/>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="competencia">Competência</Label>
                  <Input id="competencia" type="text" value={formData.competencia} onChange={handleInputChange} placeholder="MM/AAAA"/>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="gerarTresUltimas" checked={formData.gerarTresUltimas} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, gerarTresUltimas: checked }))} />
                <Label htmlFor="gerarTresUltimas">Gerar recibos para as três últimas competências</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Resumo dos Cálculos</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 p-4 bg-gray-100 rounded-md">
                <Label>INSS (11%)</Label>
                <p className="text-lg font-mono text-red-600">- {calculatedValues.inss.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
              </div>
              <div className="space-y-2 p-4 bg-gray-100 rounded-md">
                <Label>IRRF</Label>
                <p className="text-lg font-mono text-red-600">- {calculatedValues.irrf.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
              </div>
              <div className="space-y-2 p-4 bg-green-50 rounded-md border border-green-200">
                <Label>Valor Líquido a Receber</Label>
                <p className="text-lg font-mono text-green-700 font-bold">{calculatedValues.liquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end pb-8">
            <Button onClick={handleGenerateRecibo} className="bg-purple-600 hover:bg-purple-700">
              Gerar Recibo
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
